import clsx from 'clsx'

import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updateShortlist } from '@disco/state-management/actions/ShortlistActions'
import { SHOWCASES } from '@disco/constants/FeatureTypes'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import { getShortlistPresentationType } from '@disco/utils/shortlist'
import { sanitize } from '@disco/utils/string-utils'
import Row from 'components/common/row'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import Switch from 'components/ui/switch'
import Tooltip from 'components/ui/tooltip'
import PlaylistMessageEdit from '../playlist-message-edit'
import { ConnectedShortlistThemeSelect } from '../playlist-theme-select'
import ChooseDesign from './choose-design'
import styles from './index.module.css'

const PlaylistPresentation = (): React.ReactElement<'div'> => {
  const routeParams = useParams<{ shortlistId: string }>()
  const params = { shortlistId: routeParams.shortlistId! }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const showcasesEnabled = useFeatureEnabled(SHOWCASES)
  const isPhone = useSelector(isDeviceTypePhone)
  const shortlist = useSelector(state =>
    getShortlistById(state, {
      id: params.shortlistId,
    }),
  )
  const contactMessage = useSelector(
    state => state.user.settings.custom_message_html,
  )
  const { show_lyrics, show_business_logo, show_contacts, showcase } = shortlist
  const presentationType = getShortlistPresentationType(shortlist)
  const disabledSettings = !showcasesEnabled && Boolean(showcase?.type)
  return (
    <div
      className={clsx({
        [styles.playlistPresentationDisabled]: disabledSettings,
      })}
    >
      <ChooseDesign shortlistId={params.shortlistId} />
      {!showcase?.type && (
        <>
          <Row columnsSize={[12]}>
            <div>
              <PlaylistMessageEdit params={params as { shortlistId: string }} />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <ConnectedShortlistThemeSelect
                shortlistId={params.shortlistId}
                navigate={navigate}
              />
            </div>
          </Row>
        </>
      )}
      {!isPhone && (
        <Row columnsSize={[12]}>
          <div>
            <Heading size={500}>Settings</Heading>
            <div
              className={styles.playlistPresentationToggleContainer}
              key="Show Business Logo"
            >
              <span className={styles.playlistPresentationToggleLabel}>
                Show Business Logo
              </span>
              <div className={styles.playlistPresentationToggle}>
                <Switch
                  disabled={disabledSettings}
                  value={show_business_logo ?? true}
                  onChange={(checked: boolean) => {
                    if (checked) {
                      dispatch(
                        sendMixpanelEvent(
                          `${presentationType} - Show business logo`,
                        ),
                      )
                    }

                    dispatch(
                      updateShortlist(params.shortlistId, {
                        show_business_logo: checked,
                      }),
                    )
                  }}
                />
              </div>
            </div>
            <div
              className={styles.playlistPresentationToggleContainer}
              key="Show contact info"
            >
              <div className="flc">
                <span className={styles.playlistPresentationToggleLabel}>
                  Show contact info
                </span>
                {contactMessage && (
                  <Tooltip
                    content={
                      <div
                        // eslint-disable-next-line react/no-danger -- markup goes through sanitize() (DOMPurify)
                        dangerouslySetInnerHTML={{
                          __html: sanitize(contactMessage),
                        }}
                      />
                    }
                  >
                    <div className="flc">
                      <Icon type="info-outline" height="12px" />
                    </div>
                  </Tooltip>
                )}
              </div>

              <div className={styles.playlistPresentationToggle}>
                <Switch
                  disabled={disabledSettings}
                  value={show_contacts}
                  onChange={(checked: boolean) => {
                    if (checked) {
                      dispatch(
                        sendMixpanelEvent(
                          `${presentationType} - Show contact info`,
                        ),
                      )
                    }

                    dispatch(
                      updateShortlist(params.shortlistId, {
                        show_contacts: checked,
                      }),
                    )
                  }}
                />
              </div>
            </div>
            <div
              className={styles.playlistPresentationToggleContainer}
              key="Show lyrics"
            >
              <span className={styles.playlistPresentationToggleLabel}>
                Show lyrics
              </span>

              <div className={styles.playlistPresentationToggle}>
                <Switch
                  disabled={disabledSettings}
                  value={show_lyrics}
                  onChange={(checked: boolean) => {
                    if (checked) {
                      dispatch(
                        sendMixpanelEvent(`${presentationType} - Show lyrics`),
                      )
                    }

                    dispatch(
                      updateShortlist(params.shortlistId, {
                        show_lyrics: checked,
                      }),
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </Row>
      )}
    </div>
  )
}

export default PlaylistPresentation
